import classNames from "classnames";
import { ListGroup, ListGroupControl, LoadingState } from "components/Shared";
import Pagination from "components/Shared/Tcp/Pagination";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { memo, useEffect, useState } from "react";
import Row from "./Row";

/**
 * The lease list itself — the control bar, the rows and the pager — shared by
 * the top-level Leases section (List.tsx) and the property workspace's Leases
 * tab (PropertyTab.tsx), the same way v2/components/Projects/List renders
 * components/Projects/Collection.
 *
 * `propertyId` set   -> the property tab: that property's leases only.
 * `propertyId` unset -> the global list.
 *
 * THERE IS NO PROPERTY PICKER. It was here and came out (Sim, 24 Aug): the
 * search box below already matches on property name, so a second, heavier way to
 * narrow by property was two controls doing one job. `?property_id=` is still a
 * real API filter — the property tab is what passes it.
 *
 * ---------------------------------------------------------------------------
 * SORT AND SEARCH ARE SERVER-SIDE, AND HAVE TO BE
 * ---------------------------------------------------------------------------
 * This list is PAGED by the API. components/Tenancies/Collection sorts and
 * filters in place, but only because Properties/Show has already loaded every
 * tenancy with `per_page: -1`; doing that here would mean fetching every lease
 * in the estate to search them. So the search box drives `?q=` and the sort
 * drives `?sort=`, and both reset to page 1 — page 4 of everything is rarely
 * page 4 of the matches, and showing an empty page reads as "no results".
 *
 * SORT_OPTIONS ARE REAL COLUMNS ONLY. SortOptionsGenerator flips to sorting in
 * Ruby for any attribute that is not a column, which turns the relation into an
 * Array and breaks paging. That rules out the fields folded from the amendment
 * tree (`starts_at`, `ends_at`, `unit_count`) and the flattened ones
 * (`property_name`, `tenant_name`) — they are searchable, not sortable.
 */

const SORT_OPTIONS = ["tcode", "lease_status", "lease_type", "created_at"];

interface Props {
	propertyId?: string;
}

const Collection = ({ propertyId }: Props) => {
	// null = the first load has not answered yet. An empty array is a legitimate
	// answer — a user whose projects are all TCP-side sees no leases at all.
	const [agreements, setAgreements] = useState<tcpinpoint.LeaseAgreement[] | null>(null);
	const [sortBy, setSortBy] = useState("tcode");
	// Two pieces of state on purpose: `searchTerm` is what the box shows and
	// updates on every keystroke, `query` is what the API is asked for and only
	// catches up after the debounce.
	const [searchTerm, setSearchTerm] = useState("");
	const [query, setQuery] = useState("");
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(
		new Datasource({
			mainModelName: "lease_agreement",
			perPage: 20,
			currentPage: 1,
			sortBy: "tcode",
			renderTrigger: renderTrigger
		})
	);

	// Created once, so the timer survives re-renders — a debounce rebuilt on every
	// keystroke never fires.
	const [debouncedSetQuery] = useState(() =>
		_.debounce((value: string) => {
			datasource.setCurrentPage(1);
			setQuery(value);
		}, 300)
	);

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setAgreements(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	// Cancel a pending debounce on unmount, or it fires setState into a component
	// that is gone.
	useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

	useEffect(() => {
		datasource.get("v2", "lease-agreements", {
			// Nils and blanks are OMITTED rather than sent: the query string is built
			// with URLSearchParams, which turns an undefined value into the literal
			// string "undefined" and would have the API filter on a property of that
			// name.
			queryStringParameters: _.omitBy(
				{
					sort: sortBy,
					per_page: datasource.perPage,
					page: datasource.currentPage,
					property_id: propertyId,
					q: query.trim() || undefined
				},
				_.isNil
			)
		});
	}, [datasource, datasource.currentPage, datasource.perPage, propertyId, sortBy, query]);

	const onSortChange = (value: string) => {
		datasource.setCurrentPage(1);
		setSortBy(value);
	};

	const onSearchChange = (value: string) => {
		setSearchTerm(value);
		debouncedSetQuery(value);
	};

	return (
		<React.Fragment>
			<ListGroupControl
				onSortChange={onSortChange}
				selectedSort={sortBy}
				sortOptions={SORT_OPTIONS}
				onSearchChange={onSearchChange}
				searchValue={searchTerm}
				searchLabel="search_leases"
			/>

			{agreements === null ? (
				<LoadingState />
			) : (
				<React.Fragment>
					{/* THE EMPTY STATE IS ListGroup'S, NOT OURS (Sim, 24 Aug). Handed an
					    empty collection it renders the shared <EmptyState resource=…/> —
					    the same "No Leases found" block, in the same place, that every
					    other list in the app shows. A hand-rolled div outside the
					    ListGroup looked like a different app: left-aligned, and above
					    where the rows would have been.

					    ListGroup's own client-side paginator is OFF: it would page within
					    the 20 rows this request returned and present that as the whole
					    set. The one below pages the API. */}
					<ListGroup
						resource="lease_agreements"
						collection={agreements}
						pagination={false}
						renderRow={(agreement: tcpinpoint.LeaseAgreement) => (
							<Row key={agreement.id} agreement={agreement} />
						)}
					/>
					{!_.isEmpty(agreements) && (
						<div className={classNames("margin-auto", "flex justify-center")}>
							<Pagination datasource={datasource} />
						</div>
					)}
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default memo(Collection);
